import React, { useCallback, useEffect, useState } from 'react';
import type { NotificationProps } from 'elmo-elements/Notification';
import { CancelIcon, CheckIcon, InfoIcon, WarningIcon } from 'elmo-elements';
import omit from 'lodash/omit';
import type { TAppToasterParams } from 'src/types';

export const initialNotification: NotificationProps = {
    isActive: false,
    type: 'dark',
};

export const useAppToaster = (props: TAppToasterParams | void) => {
    // app context params with defaults
    const notificationDuration = props?.notificationDuration ?? 4000;
    // notification state
    const [notification, setNotification] = useState<NotificationProps | undefined>();
    // close notification function handler
    const handleCloseNotification = useCallback(() => {
        setNotification((prevState) => ({
            ...prevState,
            isActive: false,
        }));
    }, [setNotification]);
    // get toaster icon
    const getToasterIcon = (type: NotificationProps['type']) => {
        if (type === 'info') {
            return <InfoIcon />;
        }

        if (type === 'warning') {
            return <WarningIcon />;
        }

        if (type === 'danger') {
            return <CancelIcon />;
        }

        return <CheckIcon />;
    };
    // effect for auto notification closing
    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout>;
        // close after notificationDuration
        if (notification?.isActive) {
            timeout = setTimeout(handleCloseNotification, notificationDuration);
        }
        return () => {
            clearTimeout(timeout);
        };
    }, [handleCloseNotification, notification, notificationDuration]);
    // open notification function handler
    const handleCallNotification = useCallback(
        (nextNotification: NotificationProps | undefined): void => {
            // to hide previous notification in a pretty-way
            handleCloseNotification();
            // to open new notification
            setTimeout(() => {
                setNotification({
                    ...initialNotification,
                    ...omit(nextNotification, ['type']),
                    icon: !nextNotification?.icon
                        ? getToasterIcon(nextNotification?.type ?? 'success')
                        : nextNotification.icon,
                    onClose: (event) => {
                        // if next notification has own onClose handler
                        if (nextNotification?.onClose) {
                            nextNotification.onClose(event);
                        }
                        // then close
                        handleCloseNotification();
                    },
                    isActive: true,
                });
            }, 400);
        },
        [handleCloseNotification]
    );
    return { notification, handleCallNotification };
};
